import { useGlobalState } from '@highlight/ipc/react'
import { PERFORMANCE_OVERLAY_FLAG_KEY } from '@shared/performance-monitor'
import { useState } from 'react'
import { DebugOverlay } from '@renderer/components/debug-overlay'
import { Metric, MetricTrendChart } from '@renderer/features/performance-monitor/metric-chart'
import {
  AX_CHART,
  DEFAULT_SORT,
  IPC_CHART,
  METRIC_CHARTS,
  THRESHOLDS,
  historyValues,
  type SortColumn,
  type SortState,
} from '@renderer/features/performance-monitor/model'
import { PerformanceTotals, ProcessTable } from '@renderer/features/performance-monitor/process-table'
import { usePerformanceSnapshots } from '@renderer/features/performance-monitor/use-performance-snapshots'
import { FadingScrollable } from '@renderer/chat/components'

export default function PerformanceMonitor() {
  const [enabled, setEnabled] = useGlobalState<boolean>(PERFORMANCE_OVERLAY_FLAG_KEY, false)
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const { snapshot, history } = usePerformanceSnapshots(enabled)

  if (!enabled || !snapshot) {
    return null
  }

  const onSort = (column: SortColumn): void => {
    setSort((previous) =>
      previous.column === column
        ? { column, descending: !previous.descending }
        : { column, descending: column !== 'label' },
    )
  }

  const { totals, processes, ipcFanout, axPolls } = snapshot
  const hasMeasuredPower = totals.powerMilliwatts !== undefined
  const visibleMetricCharts = METRIC_CHARTS.filter(({ metric }) => metric !== 'powerMilliwatts' || hasMeasuredPower)
  const slowestAxWalkMs = axPolls && axPolls.length > 0 ? Math.max(...axPolls.map(({ lastMs }) => lastMs)) : undefined
  const fanoutSourceUnit = ipcFanout?.sourceRateUnit === 'minute' ? '/min' : '/s'
  const fanoutSourceThresholds =
    ipcFanout?.sourceRateUnit === 'minute' ? THRESHOLDS.fanoutPerMinute : THRESHOLDS.fanoutPerSecond

  return (
    <DebugOverlay label="perf monitor" onClose={() => setEnabled(false)} className="w-[400px]">
      {ipcFanout && (
        <div className="p-2.5 pb-0">
          <div className="flex justify-between">
            <span className="text-foreground-placeholder">ipc fan-out (frames → every renderer)</span>
            <span>
              <Metric value={ipcFanout.framesPerSecond} thresholds={THRESHOLDS.fanoutPerSecond} />
              <span className="text-foreground-placeholder">/s</span>
            </span>
          </div>
          <FadingScrollable className="max-h-20 pb-2.5 pr-1">
            {ipcFanout.sourceRates.map(({ source, rate }) => (
              <div key={source} className="flex justify-between gap-2">
                <span className="text-foreground-secondary truncate">{source}</span>
                <span className="shrink-0">
                  <Metric value={rate} thresholds={fanoutSourceThresholds} />
                  <span className="text-foreground-placeholder">{fanoutSourceUnit}</span>
                </span>
              </div>
            ))}
          </FadingScrollable>
        </div>
      )}

      {axPolls && axPolls.length > 0 && (
        <div className="border-t p-2.5">
          <span className="text-foreground-placeholder">ax walks (last 60s)</span>
          {axPolls.map((ax) => (
            <div key={ax.service} className="flex justify-between gap-2">
              <span className="text-foreground-secondary truncate">{ax.service}</span>
              <span className="shrink-0 tabular-nums">
                <Metric value={ax.lastMs} thresholds={THRESHOLDS.axMs} />
                <span className="text-foreground-placeholder"> last · </span>
                <Metric value={ax.avgMs} thresholds={THRESHOLDS.axMs} />
                <span className="text-foreground-placeholder"> avg · </span>
                <Metric value={ax.maxMs} thresholds={THRESHOLDS.axMs} />
                <span className="text-foreground-placeholder"> max · {ax.pollsPerMinute}/min</span>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="[&>*]:border-border grid grid-cols-2 border-t [&>*:nth-child(even)]:border-l [&>*:nth-child(n+3)]:border-t">
        {visibleMetricCharts.map((config) => {
          const currentValue = totals[config.metric]
          if (currentValue === undefined) {
            return null
          }
          return (
            <MetricTrendChart
              key={config.metric}
              config={config}
              currentValue={currentValue}
              values={historyValues(history, ({ totals: sampleTotals }) => sampleTotals[config.metric])}
            />
          )
        })}
        {ipcFanout && (
          <MetricTrendChart
            config={IPC_CHART}
            currentValue={ipcFanout.framesPerSecond}
            values={historyValues(history, ({ ipcFramesPerSecond }) => ipcFramesPerSecond)}
          />
        )}
        {slowestAxWalkMs !== undefined && (
          <MetricTrendChart
            config={AX_CHART}
            currentValue={slowestAxWalkMs}
            values={historyValues(history, ({ slowestAxWalkMs: value }) => value)}
          />
        )}
      </div>

      <PerformanceTotals totals={totals} />
      <ProcessTable processes={processes} sort={sort} onSort={onSort} hasMeasuredPower={hasMeasuredPower} />
    </DebugOverlay>
  )
}
